/**
 * useConfidenceReport
 *
 * Derives a canonical `ConfidenceReport` for the current user by pulling
 * signal counts, ages, and cross-framework agreement from the tables that
 * already exist (`personality_signals`, `user_type_feedback`,
 * `user_canonical_personality`). Nothing about this is speculative — it is
 * a thin adapter that hands structured inputs to `computeConfidence`.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  computeConfidence,
  checkConfidenceReport,
  type ConfidenceReport,
} from '@/lib/confidence';

// Weights mirror `useDataSources` so the two surfaces stay in sync.
const SOURCE_BASE_WEIGHTS: Record<string, number> = {
  tps: 0.8, // specialized
  quiz: 0.7,
  liker: 0.4,
  mentor: 0.4,
  career: 0.4,
  enrichment: 0.6,
  journal: 0.4,
  shadow: 0.4,
  match_preference: 0.3,
};

export function useConfidenceReport(targetUserId?: string) {
  const { user } = useAuth();
  const userId = targetUserId || user?.id;

  return useQuery<ConfidenceReport | null>({
    queryKey: ['confidence-report', userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!userId) return null;

      const [signalsRes, feedbackRes, canonicalRes, assessmentsRes, quizRes, likerRes] = await Promise.all([
        supabase
          .from('personality_signals')
          .select('source_type, confidence, created_at, framework')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(500),
        supabase
          .from('user_type_feedback')
          .select('id, created_at, feedback')
          .eq('user_id', userId)
          .limit(50),
        supabase
          .from('user_canonical_personality')
          .select('mbti, enneagram, big_five, disc, socionics, holland, sloan, alignment, attachment, integral_level, ensemble_result, confidence_score, sources_used')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('assessments')
          .select('created_at, updated_at, variant, source_type')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('quiz_results')
          .select('created_at, updated_at, quiz_slug, result_data, typing_suggestions')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('liker_personality_inference')
          .select('updated_at, swipe_count_at_inference, inferred_mbti, inferred_enneagram')
          .eq('user_id', userId)
          .maybeSingle(),
      ]);

      const signals = (signalsRes.data ?? []) as Array<{
        source_type: string | null;
        confidence: number | null;
        created_at: string;
        framework: string | null;
      }>;

      const distinctSources = new Set(
        signals.map((s) => s.source_type ?? 'unknown').filter(Boolean),
      );
      const assessments = assessmentsRes.data ?? [];
      const completedQuizzes = (quizRes.data ?? []).filter((q) => isCompletedQuizResult(q.result_data));
      const liker = likerRes.data as
        | {
            updated_at: string | null;
            swipe_count_at_inference: number | null;
            inferred_mbti: string | null;
            inferred_enneagram: string | null;
          }
        | null;

      const canonical = (canonicalRes.data ?? {}) as Record<string, unknown>;
      const canonicalSources = Array.isArray(canonical.sources_used)
        ? canonical.sources_used.filter((s): s is string => typeof s === 'string')
        : [];
      canonicalSources.forEach((s) => distinctSources.add(s));
      if (assessments.length > 0) distinctSources.add('tps');
      if (completedQuizzes.length > 0) distinctSources.add('quiz');
      if ((liker?.swipe_count_at_inference ?? 0) > 0 || liker?.inferred_mbti || liker?.inferred_enneagram) {
        distinctSources.add('liker');
      }
      const nSources = distinctSources.size;

      const lastUpdated = latestIso([
        signals[0]?.created_at,
        assessments[0]?.updated_at,
        assessments[0]?.created_at,
        completedQuizzes[0]?.updated_at,
        completedQuizzes[0]?.created_at,
        liker?.updated_at,
      ]);
      const ageDays = lastUpdated
        ? Math.max(0, (Date.now() - new Date(lastUpdated).getTime()) / 86_400_000)
        : null;

      // Consensus: prefer real ensemble consensus strength when we have one.
      // Avoid falling back to a mean of self-reported signal confidences —
      // most signals leave `confidence` null, and averaging nulls-as-zero
      // (or partial values) produced meaningless numbers.
      const ensemble = (canonicalRes.data as any)?.ensemble_result as
        | { consensusStrength?: number; consensus?: number }
        | null
        | undefined;
      const canonicalConfidence = num(canonical.confidence_score);
      const rawConsensus =
        ensemble?.consensusStrength ?? ensemble?.consensus ?? canonicalConfidence ?? null;
      const consensusStrength =
        typeof rawConsensus === 'number' && rawConsensus >= 0 && rawConsensus <= 1
          ? rawConsensus
          : null;

      // Cross-framework agreement: real coherence checks, not "how many
      // frameworks are populated". Compares MBTI E/I ↔ Big Five Extraversion
      // and MBTI N/S ↔ Openness — these are the two mappings that are
      // documented and testable. Falls through to `null` (not-a-contributor)
      // when either side is missing rather than defaulting to 0.5.
      const crossFrameworkAgreement = computeCoherence(canonical);

      // Dispute penalty: fraction of feedback marked as "wrong" / negative.
      // Only meaningful with a handful of feedback rows — otherwise skip.
      const feedback = (feedbackRes.data ?? []) as Array<{ feedback: string | null }>;
      const bad = feedback.filter((f) => {
        const v = (f.feedback ?? '').toLowerCase();
        return v === 'wrong' || v === 'inaccurate' || v === 'disagree' || v === 'negative';
      }).length;
      const disputePenalty = feedback.length >= 2 ? bad / feedback.length : null;

      const strongestSourceWeight = Math.max(
        0,
        ...Array.from(distinctSources).map((s) => SOURCE_BASE_WEIGHTS[s] ?? 0.3),
      );

      const populatedFrameworks = [
        'mbti',
        'enneagram',
        'big_five',
        'disc',
        'socionics',
        'holland',
        'sloan',
        'alignment',
        'attachment',
        'integral_level',
      ].filter((key) => canonical[key] !== null && canonical[key] !== undefined).length;
      const frameworkCoverage = populatedFrameworks / 10;
      const swipeEvidence = Math.min(6, Math.floor((liker?.swipe_count_at_inference ?? 0) / 50));
      const evidenceCount =
        assessments.length +
        completedQuizzes.length +
        Math.min(20, Math.ceil(signals.length / 5)) +
        swipeEvidence;

      const raw = computeConfidence({
        consensusStrength,
        nSources,
        evidenceCount,
        frameworkCoverage,
        ageDays,
        crossFrameworkAgreement,
        disputePenalty,
        lastUpdated,
        strongestSourceWeight,
      });
      // Runtime sanity guard: never surface an implausible value. If the
      // pipeline emits something out of range, we render the "safe"
      // insufficient fallback instead of a nonsense percentage.
      return checkConfidenceReport(raw).safe;
    },
  });
}

export type { ConfidenceReport };

// ---------------------------------------------------------------------------

/**
 * Compute a 0..1 coherence signal from populated canonical frameworks.
 * Returns `null` when we can't verify anything (need at least MBTI + Big
 * Five to say anything real).
 */
function computeCoherence(c: Record<string, unknown>): number | null {
  const mbti = typeof c.mbti === 'string' ? c.mbti.toUpperCase() : null;
  const bigFive = (c.big_five ?? null) as
    | Record<string, unknown>
    | null;
  if (!mbti || !bigFive) return null;

  const checks: boolean[] = [];
  const ex = numFrom(bigFive, ['extraversion', 'Extraversion']);
  if (ex !== null && mbti.length >= 1) {
    const scaled = normalizeBigFive(ex);
    const mbtiE = mbti[0] === 'E';
    checks.push(mbtiE ? scaled >= 0.5 : scaled <= 0.5);
  }
  const op = numFrom(bigFive, ['openness', 'Openness', 'Openness to Experience', 'opennessToExperience']);
  if (op !== null && mbti.length >= 2) {
    const scaled = normalizeBigFive(op);
    const mbtiN = mbti[1] === 'N';
    checks.push(mbtiN ? scaled >= 0.5 : scaled <= 0.5);
  }
  if (checks.length === 0) return null;
  const agree = checks.filter(Boolean).length;
  return agree / checks.length;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function numFrom(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const direct = num(obj[key]);
    if (direct !== null) return direct;
    const nested = obj[key];
    if (nested && typeof nested === 'object') {
      const nestedScore = num((nested as Record<string, unknown>).score);
      if (nestedScore !== null) return nestedScore;
    }
  }
  return null;
}

function normalizeBigFive(value: number): number {
  if (value > 10) return Math.max(0, Math.min(1, value / 100));
  if (value > 1) return Math.max(0, Math.min(1, (value - 1) / 9));
  return Math.max(0, Math.min(1, value));
}

function latestIso(values: Array<string | null | undefined>): string | null {
  let latest: string | null = null;
  let latestTime = 0;
  for (const value of values) {
    if (!value) continue;
    const time = Date.parse(value);
    if (Number.isNaN(time)) continue;
    if (time > latestTime) {
      latestTime = time;
      latest = value;
    }
  }
  return latest;
}

function isCompletedQuizResult(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const result = value as Record<string, unknown>;
  return Boolean(result.matchName || result.matchType || result.suggestedMBTI || result.suggestedEnneagram || Object.keys(result).length > 1);
}